import { IPagination } from '@/types/general.type';
import { ApiResponse, axiosInstance } from '@/lib/axios';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { QueryConfig } from '@/lib/react-query';
import { TFetchParams } from '@/types/fetch.type';
import { TTicketingUser } from '../types/ticketing-user.type';
import { filterEmptyValues } from '@/utils/object';

export type TGetListTicketingUserResponse = ApiResponse<IPagination<TTicketingUser>>;
export type TGetListTicketingUserParams = TFetchParams & {
  roleId?: string;
};

export const getListTicketingUser = async (
  params: TGetListTicketingUserParams
): Promise<TGetListTicketingUserResponse> => {
  const url = `/user-ticketing/manage`;

  const filteredParams = filterEmptyValues(params);

  return axiosInstance.get(url, { params: filteredParams });
};

export const GET_LIST_TICKETING_USER_QUERY_KEY = 'get-list-ticketing-user';

export const getListTicketingUserQueryOptions = <TData = IPagination<TTicketingUser>>(
  params: TGetListTicketingUserParams,
  select?: (data: TGetListTicketingUserResponse) => TData
) => {
  return queryOptions({
    queryKey: [
      GET_LIST_TICKETING_USER_QUERY_KEY,
      {
        roleId: params?.roleId,
        search: params?.search,
        page: params?.page,
        limit: params?.limit,
        sortDir: params?.sortDir,
        sortBy: params?.sortBy,
      },
    ],
    queryFn: () => getListTicketingUser(params),
    select,
  });
};

// Overload when select is provided - returns transformed type
export function useListTicketingUserQuery<TData>(options: {
  params: TGetListTicketingUserParams;
  queryConfig?: QueryConfig<typeof getListTicketingUserQueryOptions<TData>>;
  select: (data: TGetListTicketingUserResponse) => TData;
}): ReturnType<typeof useQuery<TData>>;

// Overload when select is not provided - returns raw API response (even if generic is provided)
export function useListTicketingUserQuery(options: {
  params: TGetListTicketingUserParams;
  queryConfig?: QueryConfig<typeof getListTicketingUserQueryOptions<TGetListTicketingUserResponse>>;
}): ReturnType<typeof useQuery<TGetListTicketingUserResponse>>;

// Implementation
export function useListTicketingUserQuery<TData = TGetListTicketingUserResponse>({
  queryConfig,
  params,
  select,
}: {
  params: TGetListTicketingUserParams;
  queryConfig?: QueryConfig<typeof getListTicketingUserQueryOptions<TData | TGetListTicketingUserResponse>>;
  select?: (data: TGetListTicketingUserResponse) => TData;
}) {
  return useQuery({
    ...getListTicketingUserQueryOptions(params, select),
    ...queryConfig,
  }) as ReturnType<
    typeof useQuery<TData extends TGetListTicketingUserResponse ? TGetListTicketingUserResponse : TData>
  >;
}
